"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  MapPin,
  CalendarDays,
  CarFront,
  Users,
  CircleDot,
  Loader2,
  Navigation,
  LocateFixed,
  IndianRupee,
  Plus,
  X,
  XCircle,
  Map as MapIcon,
  AlertCircle,
  ArrowLeft
} from "lucide-react";

type LoggedInUser = {
  id: number;
  name: string;
  mobile: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

type ViaPoint = {
  id: string;
  location: string;
  latitude: string;
  longitude: string;
};

type RouteEstimate = {
  distanceKm: number;
  durationMinutes: number;
  durationText: string;
  routeGeometry: [number, number][];
};

type LocationResult = {
  id: number | string;
  name: string;
  latitude: number;
  longitude: number;
};

const EMPTY_ROUTE_GEOMETRY: [number, number][] = [];
const VEHICLE_TYPES = ["Bike", "Scooter", "Auto", "Car", "SUV", "Van", "Magic", "Commander", "Traveller", "Bus", "Other"];
const MAX_PASSENGER_SEATS: Record<string, number> = {
  "Bike": 1,
  "Scooter": 1,
  "Auto": 3,
  "Car": 4,
  "SUV": 7,
  "Van": 12,
  "Magic": 10,
  "Commander": 10,
  "Traveller": 26,
  "Bus": 60,
  "Other": 20,
};

// ============================================================================
// FOOLPROOF RIDE ROUTE MAP (No flickering!)
// ============================================================================

type RideRouteMapProps = {
  sharedMap?: import("leaflet").Map | null;
  fromLatitude: string;
  fromLongitude: string;
  toLatitude: string;
  toLongitude: string;
  viaPoints: ViaPoint[];
  routeGeometry: [number, number][];
  onStartChange: (latitude: number, longitude: number) => void;
  onDestinationChange: (latitude: number, longitude: number) => void;
  onViaChange: (id: string, latitude: number, longitude: number) => void;
};

function RideRouteMap({
  sharedMap,
  fromLatitude, fromLongitude, toLatitude, toLongitude,
  viaPoints, routeGeometry,
  onStartChange, onDestinationChange, onViaChange,
}: RideRouteMapProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const leafletRef = useRef<any>(null);
  const routeLayerRef = useRef<any>(null);
  const markersLayerRef = useRef<any>(null);
  const [mapReady, setMapReady] = useState(false);

  const latestCallbacks = useRef({ onStartChange, onDestinationChange, onViaChange });
  useEffect(() => {
    latestCallbacks.current = { onStartChange, onDestinationChange, onViaChange };
  }, [onStartChange, onDestinationChange, onViaChange]);

  const parsePoint = (latStr: string, lngStr: string): [number, number] | null => {
    if (latStr.trim() === "" || lngStr.trim() === "") return null;
    const lat = Number(latStr); const lng = Number(lngStr);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return [lat, lng];
  };

  useEffect(() => {
    if (!sharedMap) return;

    const homepageMap = sharedMap;
    let cancelled = false;
    let markerLayer: import("leaflet").LayerGroup | null = null;
    let routeLayer: import("leaflet").LayerGroup | null = null;

    async function attachToHomepageMap() {
      const module = await import("leaflet");
      if (cancelled) return;

      const L = module.default ?? module;

      mapInstanceRef.current = sharedMap;
      leafletRef.current = L;

      markerLayer = L.layerGroup().addTo(homepageMap);
      routeLayer = L.layerGroup().addTo(homepageMap);

      markersLayerRef.current = markerLayer;
      routeLayerRef.current = routeLayer;
      setMapReady(true);
    }

    void attachToHomepageMap();

    return () => {
      cancelled = true;
      markerLayer?.remove();
      routeLayer?.remove();

      markersLayerRef.current = null;
      routeLayerRef.current = null;
      mapInstanceRef.current = null;
      leafletRef.current = null;
      setMapReady(false);

      // Never remove the shared Homepage map.
    };
  }, [sharedMap]);

  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !leafletRef.current) return;
    const L = leafletRef.current;
    const map = mapInstanceRef.current;
    const markerLayer = markersLayerRef.current;
    const routeLayer = routeLayerRef.current;

    markerLayer.clearLayers();
    routeLayer.clearLayers();

    const startPoint = parsePoint(fromLatitude, fromLongitude);
    const destinationPoint = parsePoint(toLatitude, toLongitude);
    const validViaPoints = viaPoints.map((via, index) => ({ via, index, point: parsePoint(via.latitude, via.longitude) })).filter((item): item is { via: ViaPoint; index: number; point: [number, number] } => item.point !== null);

    const markerIcon = (text: string, background: string) => L.divIcon({
      className: "",
      html: `<div style="width:36px;height:36px;border-radius:50%;background:${background};color:white;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;">${text}</div>`,
      iconSize: [36, 36], iconAnchor: [18, 18],
    });

      // Homepage already renders the Pickup marker; avoid a duplicate blue dot.

    validViaPoints.forEach(({ via, index, point }) => {
      const marker = L.marker(point, { icon: markerIcon(String(index + 1), "#8b5cf6"), draggable: true }).addTo(markerLayer).bindPopup(`Via ${index + 1}`);
      marker.on("dragend", () => {
        const position = marker.getLatLng();
        latestCallbacks.current.onViaChange(via.id, position.lat, position.lng);
      });
    });

    if (destinationPoint) {
      const marker = L.marker(destinationPoint, { icon: markerIcon("", "#e11d48"), draggable: true }).addTo(markerLayer).bindPopup("Destination");
      marker.on("dragend", () => {
        const position = marker.getLatLng();
        latestCallbacks.current.onDestinationChange(position.lat, position.lng);
      });
    }

    const validRoute = routeGeometry.filter((p) => Array.isArray(p) && p.length >= 2 && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1]))).map((p) => [Number(p[0]), Number(p[1])] as [number, number]);
    const fallbackRoute = [...(startPoint ? [startPoint] : []), ...validViaPoints.map((item) => item.point), ...(destinationPoint ? [destinationPoint] : [])];
    const displayRoute = validRoute.length >= 2 ? validRoute : fallbackRoute;

    if (displayRoute.length >= 2) {
      L.polyline(displayRoute, { color: "white", weight: 9, opacity: 0.95 }).addTo(routeLayer);
      L.polyline(displayRoute, { color: "#4f46e5", weight: 5, opacity: 1 }).addTo(routeLayer);
    }

    const boundsPoints = [...(startPoint ? [startPoint] : []), ...validViaPoints.map((item) => item.point), ...(destinationPoint ? [destinationPoint] : []), ...validRoute];
    if (boundsPoints.length >= 2) map.fitBounds(L.latLngBounds(boundsPoints), { padding: [40, 40] });
    else if (boundsPoints.length === 1) map.setView(boundsPoints[0], 13);
  }, [fromLatitude, fromLongitude, toLatitude, toLongitude, viaPoints, routeGeometry, mapReady]);

  return null; // Markers and route render directly on the shared Homepage map.
}

function formatDuration(totalMinutes: number) {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "";
  const rounded = Math.round(totalMinutes);
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;
  if (hours <= 0) return `${minutes} min`;
  if (minutes <= 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

type HomepageOfferRidePanelProps = {
  sharedMap: import("leaflet").Map | null;
  pickup: { latitude: number; longitude: number; label: string } | null;
  destination: { latitude: number; longitude: number; label: string } | null;
  step: 1 | 2 | 3;
  onStepChange: (step: 1 | 2 | 3) => void;
  onClose: () => void;
};

export default function HomepageOfferRidePanel({
  sharedMap,
  pickup,
  onStepChange,
  destination,
  step,
  onClose,
}: HomepageOfferRidePanelProps) {
  const router = useRouter();

  const [user, setUser] = useState<LoggedInUser | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [posting, setPosting] = useState(false);

  // Ride Now-style Offer Ride navigation.
  useEffect(() => {
    setOfferStep(step);
  }, [step]);


  const [offerStep, setOfferStep] = useState<1 | 2 | 3>(1);


  // Locations state
  const [fromLocation, setFromLocation] = useState("Detecting your exact GPS location...");
  const [fromLatitude, setFromLatitude] = useState("");
  const [fromLongitude, setFromLongitude] = useState("");
  const [gettingFromLocation, setGettingFromLocation] = useState(false);
  const [fromSuggestions, setFromSuggestions] = useState<LocationResult[]>([]);

  const [toLocation, setToLocation] = useState("");
  const [toLatitude, setToLatitude] = useState("");
  const [toLongitude, setToLongitude] = useState("");
  const [gettingToLocation, setGettingToLocation] = useState(false);
  const [toSuggestions, setToSuggestions] = useState<LocationResult[]>([]);

  const [locationSearching, setLocationSearching] = useState<"from" | "to" | string | null>(null);
  const [viaPoints, setViaPoints] = useState<ViaPoint[]>([]);
  const [viaSuggestions, setViaSuggestions] = useState<Record<string, LocationResult[]>>({});

  // Ride Details
  const [rideDate, setRideDate] = useState("");
  const [rideHour, setRideHour] = useState("");
  const [rideMinute, setRideMinute] = useState("00");
  const [ridePeriod, setRidePeriod] = useState<"AM" | "PM">("AM");
  const [availableSeats, setAvailableSeats] = useState("1");
  const [contribution, setContribution] = useState("");

  // Vehicle Details
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");

  // OFFER_RIDE_VERIFIED_VEHICLE_AUTOLOAD
  // Reuse the Passenger vehicle approved during DRIVER verification.
  useEffect(() => {
    const controller = new AbortController();

    async function loadApprovedVehicle() {
      try {
        const response = await fetch("/api/driver-registration", {
          cache: "no-store",
          signal: controller.signal,
        });

        if (!response.ok) return;

        const data = await response.json();
        const registration = data.registration;

        if (
          controller.signal.aborted ||
          registration?.status !== "Approved" ||
          registration?.category !== "Passenger"
        ) return;

        setVehicleType(registration.vehicleType ?? "");
        setVehicleName(registration.vehicleModel ?? "");
        setVehicleNumber(registration.vehicleNumber ?? "");
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Unable to load approved vehicle:", error);
        }
      }
    }

    void loadApprovedVehicle();
    return () => controller.abort();
  }, []);

  // Routing
  const [routeEstimate, setRouteEstimate] = useState<RouteEstimate | null>(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState("");

  useEffect(() => { void loadCurrentUser(); }, []);

  // Destination B selected through the same Homepage search bar.
  useEffect(() => {
    if (!destination) return;
    setToLocation(destination.label);
    setToLatitude(String(destination.latitude));
    setToLongitude(String(destination.longitude));
    setToSuggestions([]);
  }, [destination]);

  // Homepage search bar is the source of Pickup A in Offer Ride.
  useEffect(() => {
    if (!pickup) return;
    setFromLocation(pickup.label);
    setFromLatitude(String(pickup.latitude));
    setFromLongitude(String(pickup.longitude));
    setFromSuggestions([]);
  }, [pickup]);

  // EXACT GPS LOGIC
  const gpsWatchIdRef = useRef<number | null>(null);
  const gpsFirstFixRef = useRef(false);

  async function applyGpsPosition(position: GeolocationPosition, type: "from" | "to" = "from") {
    const lat = Number(position.coords.latitude.toFixed(6));
    const lng = Number(position.coords.longitude.toFixed(6));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    gpsFirstFixRef.current = true;

    if (type === "from") {
      setFromLatitude(String(lat));
      setFromLongitude(String(lng));
      setFromLocation(`My Location (${lat}, ${lng})`);
    } else {
      setToLatitude(String(lat));
      setToLongitude(String(lng));
      setToLocation(`My Location (${lat}, ${lng})`);
    }

    try {
      const res = await fetch(`/api/reverse-geocode?latitude=${lat}&longitude=${lng}`, { cache: "no-store" });
      const data = await res.json();
      if (res.ok && data.address) {
        if (type === "from") setFromLocation(data.address);
        else setToLocation(data.address);
      }
    } catch {}
  }

  function stopGpsWatcher() {
    if (gpsWatchIdRef.current !== null && "geolocation" in navigator) {
      navigator.geolocation.clearWatch(gpsWatchIdRef.current);
      gpsWatchIdRef.current = null;
    }
  }

  // Pickup A is selected through the Homepage search/GPS controls.
  // Do not start a second automatic GPS watcher inside Offer Ride.

  // SEARCH LOCATION HANDLER
  async function chooseSearchLocation(type: "from" | "to" | string, queryStr?: string) {
    const q = (queryStr !== undefined ? queryStr : (type === "from" ? fromLocation : toLocation)).trim();
    if (q.length < 2 || q.includes("Detecting")) return;
    try {
      setLocationSearching(type);
      let data: any;
      try {
        const res = await fetch(`/api/location-search?q=${encodeURIComponent(q)}`, { cache: "no-store" });
        if (res.ok) data = await res.json();
        else throw new Error("Fallback required");
      } catch {
        const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=5&language=en&format=json`);
        if (res.ok) data = await res.json();
        else data = { results: [] };
      }

      const rawList = Array.isArray(data) ? data : data.results || [];
      const results = rawList.map((i: any) => {
        const buildName = i.admin1 ? `${i.name}, ${i.admin1}, ${i.country}` : (i.displayName || i.display_name || i.name);
        return {
          id: i.id || i.place_id || `${i.latitude || i.lat}-${i.longitude || i.lon}`,
          name: buildName || "Unknown Location",
          latitude: Number(i.lat || i.latitude),
          longitude: Number(i.lon || i.longitude || i.lng)
        };
      }).filter((i: any) => i.name !== "Unknown Location" && Number.isFinite(i.latitude) && Number.isFinite(i.longitude));

      if (type === "from") setFromSuggestions(results);
      else if (type === "to") setToSuggestions(results);
      else setViaSuggestions((prev) => ({ ...prev, [type]: results }));
    } catch (e) {
      console.error(e);
    } finally {
      setLocationSearching(null);
    }
  }

  useEffect(() => {
    const allViaPointsReady = viaPoints.every((via) => via.location.trim() !== "" && via.latitude.trim() !== "" && via.longitude.trim() !== "");
    if (fromLatitude === "" || fromLongitude === "" || toLatitude === "" || toLongitude === "" || !allViaPointsReady) {
      setRouteEstimate(null); setRouteError(""); return;
    }
    const timer = window.setTimeout(() => { void loadRouteEstimate(); }, 600);
    return () => window.clearTimeout(timer);
  }, [fromLatitude, fromLongitude, toLatitude, toLongitude, viaPoints]);

  async function loadCurrentUser() {
    try {
      const response = await fetch("/api/me", { cache: "no-store" });
      if (!response.ok) { router.push("/login"); return; }
      const loggedInUser = (await response.json()) as LoggedInUser;
      setUser(loggedInUser);
    } catch (error) { router.push("/login"); } finally { setLoadingUser(false); }
  }

  const getReadableAddress = useCallback(async (latitude: number, longitude: number) => {
    try {
      const params = new URLSearchParams({ latitude: String(latitude), longitude: String(longitude) });
      const response = await fetch(`/api/reverse-geocode?${params.toString()}`, { cache: "no-store" });
      const data = await response.json();
      if (!response.ok) return null;
      return String(data.address || "").trim() || null;
    } catch (error) { return null; }
  }, []);

  const updateStartFromMap = useCallback(async (latitude: number, longitude: number) => {
    setFromLatitude(String(latitude)); setFromLongitude(String(longitude));
    const address = await getReadableAddress(latitude, longitude);
    if (address) setFromLocation(address);
  }, [getReadableAddress]);

  const updateDestinationFromMap = useCallback(async (latitude: number, longitude: number) => {
    setToLatitude(String(latitude)); setToLongitude(String(longitude));
    const address = await getReadableAddress(latitude, longitude);
    if (address) setToLocation(address);
  }, [getReadableAddress]);

  const updateViaFromMap = useCallback(async (id: string, latitude: number, longitude: number) => {
    setViaPoints((current) => current.map((via) => via.id === id ? { ...via, latitude: String(latitude), longitude: String(longitude) } : via));
    const address = await getReadableAddress(latitude, longitude);
    if (address) setViaPoints((current) => current.map((via) => via.id === id ? { ...via, location: address } : via));
  }, [getReadableAddress]);

  async function getBrowserLocation(type: "from" | "to") {
    if (type === "from") setGettingFromLocation(true); else setGettingToLocation(true);

    try {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by this browser.");
        return;
      }

      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 30000,
          maximumAge: 0,
        });
      });

      await applyGpsPosition(position, type);
    } catch (err: any) {
      console.error("GPS error details:", err);
      let errorMsg = "Could not fetch your current GPS location.";
      if (err?.code === 1) errorMsg = "Please allow Location permission for this website, then try again.";
      else if (err?.code === 2) errorMsg = "Location is unavailable. Turn on your phone's Location/GPS and try again.";
      else if (err?.code === 3) errorMsg = "GPS took too long. Move near a window/outdoors and try Current GPS again.";
      alert(errorMsg);
    } finally {
      if (type === "from") setGettingFromLocation(false); else setGettingToLocation(false);
    }
  }

  function addViaPoint() {
    setViaPoints((current) => [...current, { id: `${Date.now()}-${Math.random()}`, location: "", latitude: "", longitude: "" }]);
  }

  function removeViaPoint(id: string) {
    setViaPoints((current) => current.filter((via) => via.id !== id));
    setViaSuggestions((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  }

  async function loadRouteSegment(from: [number, number], to: [number, number]) {
    const params = new URLSearchParams({ fromLatitude: String(from[0]), fromLongitude: String(from[1]), toLatitude: String(to[0]), toLongitude: String(to[1]) });
    const response = await fetch(`/api/route-estimate?${params.toString()}`, { cache: "no-store" });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Unable to calculate route.");
    const geometry: [number, number][] = Array.isArray(data.route?.coordinates) ? data.route.coordinates.filter((c: unknown): c is [number, number] => Array.isArray(c) && c.length >= 2 && Number.isFinite(Number(c[0])) && Number.isFinite(Number(c[1]))).map((c: [number, number]) => [Number(c[0]), Number(c[1])]) : [];
    return { distanceKm: Number(data.distanceKm), durationMinutes: Number(data.durationMinutes), routeGeometry: geometry };
  }

  async function loadRouteEstimate() {
    const start: [number, number] = [Number(fromLatitude), Number(fromLongitude)];
    const destination: [number, number] = [Number(toLatitude), Number(toLongitude)];
    const parsedViaPoints: [number, number][] = viaPoints.map((via) => [Number(via.latitude), Number(via.longitude)]);
    const allPoints = [start, ...parsedViaPoints, destination];

    if (allPoints.some((point) => !Number.isFinite(point[0]) || !Number.isFinite(point[1]))) { setRouteEstimate(null); return; }

    try {
      setRouteLoading(true); setRouteError("");
      let totalDistanceKm = 0; let totalDurationMinutes = 0;
      const mergedGeometry: [number, number][] = [];

      for (let index = 0; index < allPoints.length - 1; index += 1) {
        const segment = await loadRouteSegment(allPoints[index], allPoints[index + 1]);
        totalDistanceKm += segment.distanceKm; totalDurationMinutes += segment.durationMinutes;
        if (segment.routeGeometry.length > 0) {
          if (mergedGeometry.length > 0) mergedGeometry.push(...segment.routeGeometry.slice(1));
          else mergedGeometry.push(...segment.routeGeometry);
        }
      }
      setRouteEstimate({ distanceKm: totalDistanceKm, durationMinutes: totalDurationMinutes, durationText: formatDuration(totalDurationMinutes), routeGeometry: mergedGeometry });
    } catch (error) {
      console.error("Route estimate error:", error);
      setRouteEstimate(null); setRouteError(error instanceof Error ? error.message : "Unable to calculate route distance and time.");
    } finally { setRouteLoading(false); }
  }

  async function postRide() {
    if (!user) { alert("Please login first."); router.push("/login"); return; }
    if (!fromLocation.trim()) { alert("Please select starting point."); return; }
    if (!toLocation.trim()) { alert("Please select destination."); return; }

    const invalidVia = viaPoints.find((via) => via.location.trim() === "" || via.latitude.trim() === "" || via.longitude.trim() === "");
    if (invalidVia) { alert("Please select every Via point or remove the empty Via point."); return; }

    const parsedFromLatitude = Number(fromLatitude); const parsedFromLongitude = Number(fromLongitude);
    const parsedToLatitude = Number(toLatitude); const parsedToLongitude = Number(toLongitude);

    if (!Number.isFinite(parsedFromLatitude) || !Number.isFinite(parsedFromLongitude)) { alert("Please select a valid starting point."); return; }
    if (!Number.isFinite(parsedToLatitude) || !Number.isFinite(parsedToLongitude)) { alert("Please select a valid destination."); return; }
    if (!routeEstimate) { alert("Please wait for the road route calculation before offering your ride."); return; }
    if (!rideDate || !rideHour) { alert("Please select ride date and time."); return; }

    const hour12 = Number(rideHour); const minute = Number(rideMinute);
    if (!Number.isInteger(hour12) || hour12 < 1 || hour12 > 12 || !Number.isInteger(minute) || minute < 0 || minute > 59) { alert("Please select a valid ride time."); return; }

    const hour24 = ridePeriod === "AM" ? (hour12 === 12 ? 0 : hour12) : (hour12 === 12 ? 12 : hour12 + 12);
    const rideDateValue = new Date(`${rideDate}T${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);

    if (Number.isNaN(rideDateValue.getTime())) { alert("Please select a valid ride date and time."); return; }
    if (rideDateValue.getTime() <= Date.now()) { alert("Ride date and time must be in the future."); return; }

    const seats = Number(availableSeats);
    if (!Number.isInteger(seats) || seats < 1) { alert("Available seats must be at least 1."); return; }
    if (seats > (MAX_PASSENGER_SEATS[vehicleType] ?? 20)) { alert("Seats exceed the selected vehicle type limit."); return; }
    if (!vehicleType) { alert("Please select vehicle type."); return; }
    if (!VEHICLE_TYPES.includes(vehicleType)) { alert("Please select a valid vehicle type."); return; }

    const contributionAmount = Number(contribution);
    if (contribution.trim() === "" || !Number.isFinite(contributionAmount) || contributionAmount <= 0) {
      alert("Please enter a contribution amount greater than ₹0.");
      return;
    }

    try {
      setPosting(true);
      const response = await fetch("/api/rides", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fromLocation: fromLocation.trim(), fromLatitude: parsedFromLatitude, fromLongitude: parsedFromLongitude,
          toLocation: toLocation.trim(), toLatitude: parsedToLatitude, toLongitude: parsedToLongitude,
          viaPoints: viaPoints.map((via) => ({ location: via.location.trim(), latitude: Number(via.latitude), longitude: Number(via.longitude) })),
          rideDate: `${rideDate}T${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
          availableSeats: seats, contribution: contributionAmount,
          routeDistanceKm: routeEstimate.distanceKm, routeDurationMin: Math.round(routeEstimate.durationMinutes), routeGeometry: routeEstimate.routeGeometry,
          vehicleType, vehicleName: vehicleName.trim(), vehicleNumber: vehicleNumber.trim().toUpperCase(),
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) { alert(data.message || "Please login again."); router.push("/login"); return; }
        alert(data.message || "Unable to offer ride."); return;
      }

      alert("✅ Ride offered successfully.");
      onClose();
    } catch (error) {
      console.error("Offer ride error:", error);
      alert("Unable to offer ride.");
    } finally { setPosting(false); }
  }

  if (loadingUser) return <main className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></main>;

  return (
    <main className={offerStep === 3 ? "w-full min-w-0 max-h-[42dvh] overflow-y-auto rounded-2xl bg-white p-3 text-slate-900 sm:p-4" : "hidden"}>
      <div className="mx-auto w-full space-y-4">
        
        {/* Offer Ride Step 3 navigation */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-bold text-indigo-800">
            Ride Details · Step 3/3
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onStepChange(2)}
              className="rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close Offer Ride"
              className="rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold"
            >
              ×
            </button>
          </div>
        </div>

        {/* LOCATION SETUP CONTAINER (EXACT FIND RIDE COLORFUL STYLE) */}
        <div className="relative z-[1000] flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-2.5">
          
          <div className="flex min-w-0 flex-col gap-2">
            {/* Pickup A is selected using the Homepage search bar above. */}

              {/* Destination is selected using Homepage search in Step 2. */}
            </div>

            {/* VIA POINTS ADD PANEL */}
          <div className="flex items-center justify-between gap-2">
             <button type="button" onClick={addViaPoint} className="flex items-center justify-center rounded-xl bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-100">
                 <span className="w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center mr-2"><Plus className="w-3 h-3"/></span>
                 Add Stops
             </button>
             <p className="text-[11px] text-slate-500">Increase your matches by adding via points.</p>
          </div>

        </div>

        {/* VIA POINTS LIST */}
        {viaPoints.length > 0 && (
          <div className="bg-white rounded-[2rem] border border-slate-200 p-5 md:p-6 shadow-sm relative z-[900]">
            <div className="mb-5 flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
              <h2 className="text-xl font-black text-indigo-900 flex items-center"><Navigation className="w-5 h-5 mr-2 text-indigo-500"/> Via Points</h2>
              <button type="button" onClick={addViaPoint} className="rounded-xl bg-indigo-50 px-4 py-2 text-sm font-bold text-indigo-700 hover:bg-indigo-100 transition-colors border border-indigo-200">
                + Add Another
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {viaPoints.map((via, index) => (
                <div key={via.id} className="relative">
                  <div className="mb-1 flex items-center justify-between">
                    <p className="font-black text-indigo-800 text-sm">🟣 Stop {index + 1}</p>
                    <button type="button" onClick={() => removeViaPoint(via.id)} className="text-red-500 hover:bg-red-50 p-1 rounded-md transition-colors">
                      <X className="w-4 h-4"/>
                    </button>
                  </div>
                  <div className="flex items-center bg-slate-50 rounded-2xl px-3 py-2 border border-slate-200 focus-within:border-indigo-400">
                    <input
                      type="text"
                      value={via.location}
                      placeholder="Search city or stop..."
                      onChange={(e) => {
                        const val = e.target.value;
                        setViaPoints(curr => curr.map(v => v.id === via.id ? { ...v, location: val, latitude: "", longitude: "" } : v));
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); chooseSearchLocation(via.id, via.location); } }}
                      className="w-full bg-transparent text-sm font-bold outline-none text-slate-900"
                    />
                    <button
                      type="button"
                      onClick={() => chooseSearchLocation(via.id, via.location)}
                      className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-black shrink-0 ml-1"
                    >
                      Search
                    </button>
                  </div>
                  {viaSuggestions[via.id] && viaSuggestions[via.id].length > 0 && (
                    <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-y-auto max-h-48 divide-y divide-slate-50">
                      {viaSuggestions[via.id].map(loc => (
                        <button
                          key={loc.id}
                          onClick={() => {
                            setViaPoints(curr => curr.map(v => v.id === via.id ? { ...v, location: loc.name, latitude: String(loc.latitude), longitude: String(loc.longitude) } : v));
                            setViaSuggestions(prev => ({ ...prev, [via.id]: [] }));
                          }}
                          className="w-full text-left px-3 py-2 text-xs font-bold text-slate-800 hover:bg-indigo-50"
                        >
                          {loc.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Draw Offer Ride markers and route on the existing Homepage map. */}
        <RideRouteMap
          sharedMap={sharedMap}
          fromLatitude={fromLatitude}
          fromLongitude={fromLongitude}
          toLatitude={toLatitude}
          toLongitude={toLongitude}
          viaPoints={viaPoints}
          routeGeometry={routeEstimate?.routeGeometry ?? EMPTY_ROUTE_GEOMETRY}
          onStartChange={updateStartFromMap}
          onDestinationChange={updateDestinationFromMap}
          onViaChange={updateViaFromMap}
        />

        {/* SCHEDULE & VEHICLE DETAILS */}
        <div className="bg-white p-6 md:p-8 rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 space-y-8 relative z-0">
          <div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="bg-slate-50 rounded-2xl px-4 py-3 border border-slate-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Journey Date</label>
                <input type="date" min={new Date().toISOString().split('T')[0]} value={rideDate} onChange={(e) => setRideDate(e.target.value)} className="w-full bg-transparent text-base font-bold text-slate-900 outline-none" />
              </div>
              
              <div className="bg-slate-50 rounded-2xl px-4 py-3 border border-slate-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 transition-all md:col-span-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Exact Departure Time</label>
                <div className="flex gap-2">
                  <select value={rideHour} onChange={(e) => setRideHour(e.target.value)} className="flex-1 bg-white border border-slate-200 rounded-xl px-2 py-2 font-bold outline-none text-base text-center shadow-sm focus:border-indigo-400">
                    <option value="">Hr</option>{Array.from({ length: 12 }, (_, index) => index + 1).map((hour) => (<option key={hour} value={String(hour)}>{hour}</option>))}
                  </select>
                  <select value={rideMinute} onChange={(e) => setRideMinute(e.target.value)} className="flex-1 bg-white border border-slate-200 rounded-xl px-2 py-2 font-bold outline-none text-base text-center shadow-sm focus:border-indigo-400">
                    {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map((min) => (<option key={min} value={min}>{min}</option>))}
                  </select>
                  <select value={ridePeriod} onChange={(e) => setRidePeriod(e.target.value as "AM" | "PM")} className="flex-1 bg-white border border-slate-200 rounded-xl px-2 py-2 font-bold outline-none text-base text-center shadow-sm focus:border-indigo-400">
                    <option value="AM">AM</option><option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="bg-slate-50 rounded-2xl px-4 py-3 border border-slate-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Seats Available</label>
                <input type="number" min="1" max={MAX_PASSENGER_SEATS[vehicleType] ?? 20} value={availableSeats} onChange={(e) => setAvailableSeats(e.target.value)} className="w-full bg-transparent text-base font-black text-slate-900 outline-none" />
              </div>
              <div className="bg-slate-50 rounded-2xl px-4 py-3 border border-slate-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Contribution (₹) / Seat</label>
                <input type="text" inputMode="decimal" placeholder="Required amount (₹)" required aria-required="true" value={contribution} onChange={(e) => { const val = e.target.value; if (val === "" || /^\d*(\.\d{0,2})?$/.test(val)) setContribution(val); }} className="w-full bg-transparent text-base font-black text-slate-900 outline-none" />
              </div>
              
              
            </div>
            
            
          </div>
        </div>

        {/* ACTIONS */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <button type="button" onClick={onClose} className="rounded-2xl border border-slate-200 bg-white px-6 py-3.5 text-center font-black text-slate-600 hover:bg-slate-50 shadow-sm transition-all flex items-center justify-center">
            Cancel
          </button>
          <button
            type="button"
            onClick={postRide}
            disabled={posting || routeLoading || routeEstimate === null}
            className="rounded-2xl bg-indigo-600 px-6 py-3.5 font-black text-white hover:bg-indigo-700 disabled:bg-indigo-300 shadow-lg shadow-indigo-600/20 active:scale-95 transition-all flex items-center justify-center text-base"
          >
            {posting ? <Loader2 className="w-6 h-6 animate-spin mr-2"/> : !routeEstimate ? <MapIcon className="w-5 h-5 mr-2"/> : <CarFront className="w-6 h-6 mr-2"/>}
            {posting ? "Posting Ride..." : routeLoading ? "Calculating Route..." : !routeEstimate ? "Select Route First" : "Post Ride Offer"}
          </button>
        </div>

      </div>
    </main>
  );
}